import { useState, useRef } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'

interface TranscriptDisplayProps {
  callId: Id<'calls'>
  defaultCollapsed?: boolean
}

/**
 * Real-time conversation transcript display
 * Shows AI and caregiver messages in reverse chronological order (newest at top)
 * Supports collapsed mode showing only last 2 messages
 */
export function TranscriptDisplay({ callId, defaultCollapsed = false }: TranscriptDisplayProps) {
  const call = useQuery(api.calls.getCallById, { callId })
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)

  if (!call) {
    return (
      <div style={containerStyles}>
        <div style={loadingStyles}>Loading conversation...</div>
      </div>
    )
  }

  const transcript = call.transcript || []
  // Reverse order: newest messages at top
  const reversedTranscript = [...transcript].reverse()
  const displayedMessages = isCollapsed ? reversedTranscript.slice(0, 2) : reversedTranscript
  const hiddenCount = isCollapsed ? Math.max(0, transcript.length - 2) : 0

  return (
    <div style={getContainerStyles(isCollapsed)}>
      <div
        style={headerStyles}
        onClick={() => setIsCollapsed(!isCollapsed)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setIsCollapsed(!isCollapsed)}
      >
        <span>Conversation</span>
        <div style={headerRightStyles}>
          {transcript.length > 0 && (
            <span style={messageCountStyles}>{transcript.length} messages</span>
          )}
          <span style={toggleIconStyles}>{isCollapsed ? '▼' : '▲'}</span>
        </div>
      </div>

      <div ref={scrollRef} style={getTranscriptContainerStyles(isCollapsed)}>
        {transcript.length === 0 ? (
          <div style={emptyStyles}>Waiting for conversation to start...</div>
        ) : (
          <>
            {displayedMessages.map((message, index) => (
              <div
                key={`msg-${transcript.length - index - 1}`}
                style={{
                  ...messageStyles,
                  ...(message.speaker === 'ai' ? aiMessageStyles : caregiverMessageStyles),
                  ...(index === 0 ? newestMessageStyles : {}),
                }}
              >
                <div style={speakerStyles}>
                  {message.speaker === 'ai' ? 'Little Sister:' : 'You:'}
                  {index === 0 && <span style={newBadgeStyles}>Latest</span>}
                </div>
                <div style={textStyles}>{message.text}</div>
                <div style={timestampStyles}>
                  {new Date(message.timestamp).toLocaleTimeString()}
                </div>
              </div>
            ))}
            {isCollapsed && hiddenCount > 0 && (
              <div style={hiddenCountStyles} onClick={() => setIsCollapsed(false)}>
                {hiddenCount} older message{hiddenCount !== 1 ? 's' : ''} — tap to expand
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Styles
const containerStyles: React.CSSProperties = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '400px',
}

const getContainerStyles = (isCollapsed: boolean): React.CSSProperties => ({
  fontFamily: 'system-ui, -apple-system, sans-serif',
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: isCollapsed ? 'auto' : '400px',
  maxHeight: isCollapsed ? '200px' : '400px',
  transition: 'all 0.3s ease',
})

const headerStyles: React.CSSProperties = {
  padding: '12px 16px',
  backgroundColor: '#f9fafb',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '14px',
  fontWeight: 600,
  color: '#374151',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
}

const headerRightStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}

const messageCountStyles: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 500,
  color: '#6b7280',
}

const toggleIconStyles: React.CSSProperties = {
  fontSize: '10px',
  color: '#6b7280',
}

const getTranscriptContainerStyles = (isCollapsed: boolean): React.CSSProperties => ({
  flex: 1,
  overflowY: 'auto',
  padding: isCollapsed ? '12px' : '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: isCollapsed ? '8px' : '12px',
})

const messageStyles: React.CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  animation: 'fadeIn 0.3s ease-in',
}

const aiMessageStyles: React.CSSProperties = {
  backgroundColor: '#eff6ff',
  border: '1px solid #dbeafe',
}

const caregiverMessageStyles: React.CSSProperties = {
  backgroundColor: '#f0fdf4',
  border: '1px solid #dcfce7',
}

const speakerStyles: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: '4px',
}

const textStyles: React.CSSProperties = {
  fontSize: '14px',
  color: '#1f2937',
  lineHeight: '1.5',
}

const timestampStyles: React.CSSProperties = {
  fontSize: '11px',
  color: '#9ca3af',
  marginTop: '4px',
}

const emptyStyles: React.CSSProperties = {
  textAlign: 'center',
  color: '#9ca3af',
  fontSize: '14px',
  padding: '32px',
}

const loadingStyles: React.CSSProperties = {
  textAlign: 'center',
  color: '#6b7280',
  fontSize: '14px',
  padding: '32px',
}

const hiddenCountStyles: React.CSSProperties = {
  textAlign: 'center',
  color: '#3b82f6',
  fontSize: '12px',
  fontWeight: 500,
  padding: '8px',
  backgroundColor: '#eff6ff',
  borderRadius: '6px',
  cursor: 'pointer',
  marginTop: '4px',
}

const newestMessageStyles: React.CSSProperties = {
  borderWidth: '2px',
}

const newBadgeStyles: React.CSSProperties = {
  marginLeft: '8px',
  fontSize: '10px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#3b82f6',
  padding: '2px 6px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}
